import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
import { fetchGalleryWithPosts } from "../utils/fetchGalleryWithPosts";
import sfRoundedBold from "../assets/fonts/SF-Pro-Rounded-Bold.otf";
import sfRoundedMedium from "../assets/fonts/SF-Pro-Rounded-Medium.otf";
import sfRoundedLight from "../assets/fonts/SF-Pro-Rounded-Light.otf";

/** Firestore collection for `/view/{code}` resolution (see GalleryViewShortLinkService on iOS). */
const FIRESTORE_COLLECTION_GALLERY_VIEW_SHORT_CODES = "galleryViewShortCodes";

const ACCENT = "#f700ff";
const BG_TOP = "#05050b";
const BG_BOTTOM = "#1c0930";
const PAGE_BG = `linear-gradient(180deg, ${BG_TOP} 0%, ${BG_BOTTOM} 100%)`;

const DEFAULT_THUMB =
  "https://firebasestorage.googleapis.com/v0/b/pokefinder-9c71b.appspot.com/o/logo_img%2Fmoodsmiley.png?alt=media";

const SHARE_DESCRIPTION = "View my dumps";

/** Firestore doc may use galleryId / galleryID or non-string values. */
function galleryIdFromShortCodeDoc(data) {
  if (!data || typeof data !== "object") return "";
  const raw = data.galleryId ?? data.galleryID ?? data.GalleryId;
  if (typeof raw === "string") return raw.trim();
  if (raw != null && raw !== "") return String(raw).trim();
  return "";
}

function setMetaProperty(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaName(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function galleryTitleFromVal(gallery) {
  const name = gallery?.public?.galleryInfo?.name;
  if (typeof name === "string" && name.trim()) return name.trim();
  return "Gallery";
}

/** Cover image (`postImgURL`) when set; otherwise newest post (posts are newest-first). */
function thumbForOgAndApp(gallery, posts) {
  const cover = gallery?.public?.galleryInfo?.postImgURL;
  if (typeof cover === "string" && cover.trim()) return cover.trim();
  const firstPost = posts?.[0];
  if (firstPost?.imgURL && typeof firstPost.imgURL === "string") {
    return firstPost.imgURL.trim();
  }
  return "";
}

/**
 * Landing page for short share URLs: `https://moodstream.me/view/{shortCode}`.
 *
 * Wire in the router (e.g. App.js):
 * `<Route path="/view/:shortCode" element={<GalleryViewShortLink />} />`
 *
 * Flow: Firestore `galleryViewShortCodes/{code}` → `galleryId` → RTDB galleries + posts.
 */
export default function GalleryViewShortLink() {
  const { shortCode } = useParams();
  const [appOpening, setAppOpening] = useState(false);

  /** loading | resolved | missing | firestore-error */
  const [resolvePhase, setResolvePhase] = useState("loading");
  const [resolvedGalleryId, setResolvedGalleryId] = useState("");
  const [gallery, setGallery] = useState(null);
  /** null = not loaded yet; [] = loaded empty */
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    const code = typeof shortCode === "string" ? shortCode.trim() : "";
    if (!code) {
      setResolvePhase("missing");
      setResolvedGalleryId("");
      setGallery(null);
      setPosts(null);
      return;
    }

    let cancelled = false;
    setResolvePhase("loading");
    setResolvedGalleryId("");
    setGallery(null);
    setPosts(null);

    (async () => {
      try {
        const snap = await getDoc(
          doc(db, FIRESTORE_COLLECTION_GALLERY_VIEW_SHORT_CODES, code)
        );
        if (cancelled) return;
        if (!snap.exists()) {
          setResolvePhase("missing");
          return;
        }
        const data = snap.data();
        const gid = galleryIdFromShortCodeDoc(data);
        if (!gid) {
          setResolvePhase("missing");
          return;
        }
        setResolvedGalleryId(gid);
        setResolvePhase("resolved");
        setPosts(null);
        const { gallery: g, posts: p } = await fetchGalleryWithPosts(gid);
        if (cancelled) return;
        setGallery(g ?? null);
        setPosts(Array.isArray(p) ? p : []);
      } catch (err) {
        const errCode = err?.code;
        if (!cancelled) {
          if (
            errCode === "permission-denied" ||
            errCode === "failed-precondition" ||
            errCode === "unauthenticated"
          ) {
            setResolvePhase("firestore-error");
          } else {
            setResolvePhase("missing");
          }
          setGallery(null);
          setPosts([]);
          console.error("[GalleryViewShortLink]", errCode, err);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shortCode]);

  const displayTitle = useMemo(
    () => galleryTitleFromVal(gallery),
    [gallery]
  );

  const ogImageUrl = useMemo(() => {
    const t = thumbForOgAndApp(gallery, posts);
    return t || DEFAULT_THUMB;
  }, [gallery, posts]);

  const thumbnailForAppLink = useMemo(
    () => thumbForOgAndApp(gallery, posts),
    [gallery, posts]
  );

  const universalLink = useMemo(() => {
    if (!resolvedGalleryId) return "";
    let link = `https://app.moodstream.photos/galleryView?galleryID=${encodeURIComponent(resolvedGalleryId)}`;
    if (thumbnailForAppLink && thumbnailForAppLink !== DEFAULT_THUMB) {
      link += `&thumbnail=${encodeURIComponent(thumbnailForAppLink)}`;
    }
    link += `&title=${encodeURIComponent(displayTitle)}`;
    return link;
  }, [resolvedGalleryId, thumbnailForAppLink, displayTitle]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById("root");
    const prev = {
      htmlBg: html.style.background,
      htmlBgColor: html.style.backgroundColor,
      htmlMinH: html.style.minHeight,
      bodyBg: body.style.background,
      bodyBgColor: body.style.backgroundColor,
      bodyMinH: body.style.minHeight,
      rootBg: root?.style.background ?? "",
    };
    html.style.background = PAGE_BG;
    html.style.backgroundColor = BG_BOTTOM;
    html.style.minHeight = "100%";
    body.style.background = PAGE_BG;
    body.style.backgroundColor = BG_BOTTOM;
    body.style.minHeight = "100%";
    if (root) {
      root.style.minHeight = "100%";
      root.style.background = "transparent";
    }
    return () => {
      html.style.background = prev.htmlBg;
      html.style.backgroundColor = prev.htmlBgColor;
      html.style.minHeight = prev.htmlMinH;
      body.style.background = prev.bodyBg;
      body.style.backgroundColor = prev.bodyBgColor;
      body.style.minHeight = prev.bodyMinH;
      if (root) root.style.background = prev.rootBg;
    };
  }, []);

  useEffect(() => {
    if (resolvePhase !== "resolved" || !resolvedGalleryId) return;

    const pageUrl = typeof window !== "undefined" ? window.location.href : "";

    document.title = displayTitle;

    const metaByProperty = [
      { property: "og:type", content: "website" },
      { property: "og:title", content: displayTitle },
      { property: "og:description", content: SHARE_DESCRIPTION },
      { property: "og:image", content: ogImageUrl },
      { property: "og:image:secure_url", content: ogImageUrl },
      { property: "og:url", content: pageUrl },
    ];

    const metaByName = [
      { name: "description", content: SHARE_DESCRIPTION },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: displayTitle },
      { name: "twitter:description", content: SHARE_DESCRIPTION },
      { name: "twitter:image", content: ogImageUrl },
    ];

    metaByProperty.forEach(({ property, content }) =>
      setMetaProperty(property, content)
    );
    metaByName.forEach(({ name, content }) => setMetaName(name, content));
    setCanonical(pageUrl);

    const theme = document.querySelector('meta[name="theme-color"]');
    if (!theme) {
      const m = document.createElement("meta");
      m.setAttribute("name", "theme-color");
      m.setAttribute("content", BG_BOTTOM);
      document.head.appendChild(m);
    } else {
      theme.setAttribute("content", BG_BOTTOM);
    }

    return () => {
      document.title = "MoodStream";
    };
  }, [resolvePhase, resolvedGalleryId, displayTitle, ogImageUrl]);

  const handleOpenApp = (e) => {
    e?.preventDefault();
    if (!universalLink) return;
    setAppOpening(true);
    window.location.href = universalLink;
    setTimeout(() => setAppOpening(false), 2000);
  };

  if (resolvePhase === "missing" || resolvePhase === "firestore-error") {
    const message =
      resolvePhase === "firestore-error"
        ? "Could not load this link. Check Firestore rules allow public read on galleryViewShortCodes/{code}."
        : "This link is invalid or has expired.";
    return (
      <>
        <style>{`
          .gvsl-missing {
            min-height: 100vh;
            min-height: 100dvh;
            background: ${PAGE_BG};
            background-color: ${BG_BOTTOM};
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 24px;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            color: rgba(255,255,255,0.85);
            text-align: center;
          }
        `}</style>
        <div className="gvsl-missing">
          <p style={{ fontSize: 16 }}>{message}</p>
        </div>
      </>
    );
  }

  const showGalleryUi =
    resolvePhase === "resolved" && resolvedGalleryId && posts !== null;
  const showLoadingUi =
    resolvePhase === "loading" ||
    (resolvePhase === "resolved" && posts === null);

  return (
    <>
      <style>{`
        @font-face {
          font-family: 'SF Pro Rounded Bold';
          src: url(${sfRoundedBold}) format('opentype');
          font-weight: normal; font-style: normal; font-display: block;
        }
        @font-face {
          font-family: 'SF Pro Rounded Medium';
          src: url(${sfRoundedMedium}) format('opentype');
          font-weight: normal; font-style: normal; font-display: block;
        }
        @font-face {
          font-family: 'SF Pro Rounded Light';
          src: url(${sfRoundedLight}) format('opentype');
          font-weight: normal; font-style: normal; font-display: block;
        }

        @keyframes gvsl-spin { to { transform: rotate(360deg); } }

        * { box-sizing: border-box; margin: 0; padding: 0; }

        .gvsl-page {
          min-height: 100vh;
          min-height: 100dvh;
          background: ${PAGE_BG};
          background-color: ${BG_BOTTOM};
          display: flex;
          flex-direction: column;
          align-items: center;
          color: #fff;
          font-family: 'SF Pro Rounded Medium', -apple-system, BlinkMacSystemFont, sans-serif;
          padding-bottom: 140px;
        }

        .gvsl-inner {
          width: 100%;
          max-width: 420px;
          display: flex;
          flex-direction: column;
          padding: 28px 12px 0;
        }

        .gvsl-title {
          font-family: 'SF Pro Rounded Bold', -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 30px;
          font-weight: normal;
          color: #fff;
          line-height: 1.2;
          letter-spacing: -0.02em;
          word-break: break-word;
          overflow-wrap: break-word;
          margin-bottom: 20px;
          text-align: center;
        }

        .gvsl-posts {
          display: flex;
          flex-direction: column;
          gap: 30px;
          align-items: center;
        }
        .gvsl-post {
          width: 85%;
          border-radius: 70px;
          overflow: hidden;
          background: #160025;
        }
        .gvsl-post img {
          width: 100%;
          display: block;
          object-fit: cover;
          opacity: 0;
          transition: opacity 0.18s ease-out;
        }
        .gvsl-post img.loaded { opacity: 1; }

        .gvsl-posts-loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 16px;
          padding: 48px 24px;
          min-height: 40vh;
        }
        .gvsl-loading-text {
          font-size: 17px;
          color: rgba(255,255,255,0.75);
          text-align: center;
        }

        .gvsl-footer {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: rgba(8, 0, 15, 0.88);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-top: 1px solid rgba(247, 0, 255, 0.15);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 18px;
          padding: 12px 24px 22px;
        }
        .gvsl-footer-label {
          font-family: 'SF Pro Rounded Medium', -apple-system, BlinkMacSystemFont, sans-serif;
          font-size: 17px;
          color: rgba(255,255,255,0.7);
          line-height: 1.35;
          text-align: center;
          white-space: normal;
          max-width: 360px;
        }
        .gvsl-footer-label strong {
          font-family: 'SF Pro Rounded Bold', -apple-system, BlinkMacSystemFont, sans-serif;
          color: #fff;
          font-weight: normal;
        }
        .gvsl-footer-inner {
          width: 100%;
          max-width: 360px;
        }
        .gvsl-cta {
          font-family: 'SF Pro Rounded Bold', -apple-system, BlinkMacSystemFont, sans-serif;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          width: 100%;
          padding: 18px 28px;
          border: none;
          border-radius: 999px;
          background: ${ACCENT};
          color: #fff;
          font-size: 17px;
          font-weight: normal;
          cursor: pointer;
          transition: filter 0.2s ease, transform 0.15s ease;
        }
        .gvsl-cta:hover:not(:disabled) {
          filter: brightness(1.08);
          transform: translateY(-1px);
        }
        .gvsl-cta:disabled {
          opacity: 0.75;
          cursor: not-allowed;
        }
        .gvsl-cta-arrow {
          font-size: 1.15em;
          line-height: 1;
        }
      `}</style>

      <div className="gvsl-page">
        {showLoadingUi ? (
          <div className="gvsl-posts-loading" style={{ minHeight: "70dvh" }}>
            <p className="gvsl-loading-text">Loading gallery…</p>
            <div
              style={{
                width: 40,
                height: 40,
                border: `3px solid rgba(247, 0, 255, 0.2)`,
                borderTop: `3px solid ${ACCENT}`,
                borderRadius: "50%",
                animation: "gvsl-spin 0.9s linear infinite",
              }}
            />
          </div>
        ) : showGalleryUi ? (
          <>
            <div className="gvsl-inner">
              <h1 className="gvsl-title">{displayTitle}</h1>

              <div className="gvsl-posts">
                {posts.length > 0 ? (
                  posts.map((post, index) =>
                    post?.imgURL ? (
                      <div className="gvsl-post" key={index}>
                        <img
                          src={post.imgURL}
                          alt={`Post ${index + 1}`}
                          onLoad={(e) => e.target.classList.add("loaded")}
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      </div>
                    ) : null
                  )
                ) : (
                  <div className="gvsl-post">
                    <img
                      src={ogImageUrl}
                      alt=""
                      referrerPolicy="no-referrer"
                      onLoad={(e) => e.target.classList.add("loaded")}
                      onError={(e) => {
                        e.target.src = DEFAULT_THUMB;
                      }}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="gvsl-footer">
              <p className="gvsl-footer-label">
                Open <strong>{displayTitle}</strong> in MoodStream
              </p>
              <div className="gvsl-footer-inner">
                <button
                  type="button"
                  className="gvsl-cta"
                  onClick={handleOpenApp}
                  disabled={appOpening || !universalLink}
                >
                  {appOpening ? "Opening…" : "Open in app"}
                  <span className="gvsl-cta-arrow" aria-hidden>
                    →
                  </span>
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="gvsl-posts-loading">
            <div
              style={{
                width: 32,
                height: 32,
                border: `3px solid rgba(247, 0, 255, 0.2)`,
                borderTop: `3px solid ${ACCENT}`,
                borderRadius: "50%",
                animation: "gvsl-spin 0.9s linear infinite",
              }}
            />
          </div>
        )}
      </div>
    </>
  );
}
